import React, { useState } from 'react';
import SkeletonBar from './SkeletonBar';

/*
  HealthScore — financial health score panel for the life-finance right column.
  Props:
    healthScore  — financeHealthScore object from App.js state (or null)
    scoreLoading — boolean, shows skeleton while fetching
*/
const HealthScore = ({ healthScore, scoreLoading }) => {
  const [expandedScoreComponent, setExpandedScoreComponent] = useState(null);

  if (scoreLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[80, 60, 45].map(w => (
          <SkeletonBar key={w} width={`${w}%`} />
        ))}
      </div>
    );
  }

  if (!healthScore?.score) {
    return (
      <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', textAlign: 'center', lineHeight: 1.6, margin: 0 }}>
        Upload a document to get your score
      </p>
    );
  }

  const s = healthScore.score;
  const scoreColor = s >= 80 ? 'var(--accent)' : s >= 60 ? '#10B981' : s >= 40 ? '#F59E0B' : '#EF4444';
  const statusLabel = s >= 80 ? 'Excellent' : s >= 60 ? 'On track' : s >= 40 ? 'Getting there' : 'Needs attention';
  const componentNames = {
    savingsRate: 'Savings Rate',
    emergencyFund: 'Emergency Fund',
    expenseControl: 'Expense Control',
    incomeStability: 'Income Stability',
    dataCompleteness: 'Data Quality',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Score display */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '3px', marginBottom: '4px' }}>
          <span style={{ fontSize: '48px', fontWeight: 700, color: scoreColor, lineHeight: 1 }}>{s}</span>
          <span style={{ fontSize: '18px', color: 'var(--text-tertiary)', fontWeight: 500 }}>/100</span>
        </div>
        <p style={{ fontSize: '12px', fontWeight: 600, color: scoreColor, margin: 0, letterSpacing: '0.03em' }}>{statusLabel}</p>
      </div>

      {/* Component bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Object.entries(healthScore.components).map(([key, c]) => {
          const ratio = c.score / c.max;
          const barColor = ratio >= 0.8 ? 'var(--accent)' : ratio >= 0.6 ? '#10B981' : ratio >= 0.4 ? '#F59E0B' : '#EF4444';
          const isOpen = expandedScoreComponent === key;

          const getExplanation = () => {
            const v = c.value;
            if (key === 'savingsRate') {
              const display = v != null ? v.toFixed(1) + '%' : '—';
              if (c.score === 20) return `You save ${display} of your income — above the 30% benchmark.`;
              if (c.score === 15) return `You save ${display} of your income. Reaching 30% would strengthen your score.`;
              return `You save ${display} of your income. Even a 2-3% increase compounds over time.`;
            }
            if (key === 'emergencyFund') {
              const display = v != null ? v.toFixed(1) + ' months' : '—';
              if (c.score === 20) return `Your balance covers ${display} of expenses — a strong safety net.`;
              if (c.score === 15) return `Your balance covers ${display}. The benchmark is 6 months.`;
              return `Your balance covers ${display}. Build this before investing more.`;
            }
            if (key === 'expenseControl') {
              const display = v != null ? v.toFixed(1) + '%' : '—';
              if (c.score === 20) return `Expenses are ${display} of income — healthy. Room to invest more.`;
              if (c.score === 15) return `Expenses are ${display}. Small reductions will improve this.`;
              return `Expenses are ${display}. Review your top 2 spending categories.`;
            }
            if (key === 'incomeStability') {
              return c.score >= 15
                ? 'Income pattern looks consistent — stable foundation for planning.'
                : 'Income varies month to month. A larger buffer helps smooth lean periods.';
            }
            if (key === 'dataCompleteness') {
              if (c.score === 20) return 'All key data captured. Score reflects your full picture.';
              return `Upload more documents to improve accuracy. Missing: ${4 - v} data points.`;
            }
            return '';
          };

          return (
            <div key={key}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.02)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              style={{ borderRadius: '4px', padding: '4px 2px', transition: 'background-color 0.15s' }}
            >
              <div
                onClick={() => setExpandedScoreComponent(isOpen ? null : key)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', cursor: 'pointer' }}
              >
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{isOpen ? '▾' : '▸'}</span>
                  {componentNames[key]}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{c.score}/{c.max}</span>
              </div>
              <div style={{ height: '3px', borderRadius: '2px', backgroundColor: 'var(--bg-secondary)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(c.score / c.max) * 100}%`, backgroundColor: barColor, borderRadius: '2px' }} />
              </div>
              {isOpen && (
                <>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.65, paddingTop: '8px', margin: 0 }}>
                    {getExplanation()}
                  </p>
                  {key === 'emergencyFund' && c.trajectory && c.trajectory.status !== 'achieved' && (
                    <p style={{ fontSize: '11px', lineHeight: 1.5, paddingTop: '6px', margin: 0, color: c.trajectory.status === 'declining' ? '#EF4444' : '#10B981' }}>
                      {c.trajectory.status === 'building'
                        ? `At your current savings rate you will reach 6 months in ${c.trajectory.monthsToTarget} months (${c.trajectory.targetDate}).`
                        : 'Warning: expenses exceed income. Emergency fund is shrinking.'}
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HealthScore;
